import { Icon, InputGroup, Intent, ISliderProps, Slider, Tooltip } from '@blueprintjs/core'
import classNames from 'classnames'
import React, { KeyboardEvent, PureComponent, SyntheticEvent } from 'react'
import { TranslationFunction } from 'react-i18next'

import { i18nKeys } from '../../../app.i18n'
import { Button, identity, isPresent, Keycodes } from '../../../shared'

import './simple-slider.scss'

export type SimpleSliderProps = {
  readOnly?: boolean
  enableAdvancedMode?: boolean
  advancedOffset?: number
  offsetText?: string
  hideToggleInput?: boolean
  /** shown only if readOnly */
  valueText?: string
  translate?: TranslationFunction
} & ISliderProps

type State = {
  sliderInternalValue: number
  calculatedOutputValue: number
  toggleInputMethod: boolean
  min: number
  max: number
}

const SLIDER_DEFAULT_MAX_VALUE = 150

export class SimpleSlider extends PureComponent<SimpleSliderProps, State> {
  state = {
    sliderInternalValue: this.props.value || 0, // this value is recalculated in cdm method for advanced mode
    calculatedOutputValue: this.props.value || 0, // this value is recalculated in cdm method for advanced mode
    toggleInputMethod: false,
    min: this.props.min || 0,
    max: this.props.max || SLIDER_DEFAULT_MAX_VALUE,
  }

  componentDidMount() {
    const { onChange, advancedOffset = 0, value = 0, max = SLIDER_DEFAULT_MAX_VALUE } = this.props

    // +one re-render because of this calculation
    this.setState(this.calculateNewState(value, advancedOffset, max))

    if (!isPresent(onChange)) {
      return
    }
    onChange && onChange(this.state.calculatedOutputValue)
  }

  componentWillReceiveProps(newProps: SimpleSliderProps) {
    if (newProps.value !== this.props.value || newProps.advancedOffset !== this.props.advancedOffset) {
      const { advancedOffset = 0, value = 0, max = SLIDER_DEFAULT_MAX_VALUE } = newProps

      this.setState(this.calculateNewState(value, advancedOffset, max))
    }
  }

  render() {
    const {
      translate = identity,
      readOnly = false,
      max = SLIDER_DEFAULT_MAX_VALUE,
      hideToggleInput = false,
      disabled = false,
    } = this.props
    const { toggleInputMethod, calculatedOutputValue } = this.state

    return (
      <div>
        {!readOnly &&
          !hideToggleInput && (
            <Tooltip
              className="simple-slider__input-switch"
              content={translate(i18nKeys.shared.simpleSlider.toggleInput)}
              position="top-left"
            >
              <Button
                className="bp3-minimal"
                icon={<Icon icon="text-highlight" intent={Intent.PRIMARY} title={false} />}
                onClick={this.handleToggleInputMethodClick}
                intent={Intent.PRIMARY}
                disabled={readOnly || disabled}
              />
            </Tooltip>
          )}

        {toggleInputMethod && (
          <InputGroup
            type="number"
            value={String(calculatedOutputValue)}
            min={0}
            max={max}
            onChange={this.handleNumericInputChange}
            disabled={readOnly || disabled}
            onKeyDown={this.handleKeyDown}
          />
        )}

        {!toggleInputMethod && this.renderSlider()}
      </div>
    )
  }

  private handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === Keycodes.Enter) {
      event.preventDefault()
    }
  }

  private renderSlider = () => {
    const {
      enableAdvancedMode = false,
      advancedOffset = 0,
      offsetText,
      valueText,
      readOnly = false,
      disabled = false,
    } = this.props
    const { sliderInternalValue, calculatedOutputValue, min, max } = this.state
    const css = classNames('simple-slider', readOnly ? 'simple-slider__readonly' : '')

    if (enableAdvancedMode) {
      const advancedCss = classNames(css, {
        'simple-slider__above': sliderInternalValue > 0,
        'simple-slider__same': sliderInternalValue === 0,
        'simple-slider__below': sliderInternalValue < 0,
      })

      return (
        <span onMouseDown={this.handleDragStart}>
          <div>
            <span>
              {offsetText} {advancedOffset}%
            </span>
            {readOnly && (
              <span className="simple-slider__value-text">
                {valueText} {calculatedOutputValue}%
              </span>
            )}
          </div>

          <Slider
            {...this.props}
            className={advancedCss}
            onChange={this.handleAdvancedSliderValueChange}
            value={sliderInternalValue}
            min={min}
            max={max}
            labelRenderer={this.advancedRenderer}
            disabled={readOnly || disabled}
          />
        </span>
      )
    }

    return (
      <span onMouseDown={this.handleDragStart}>
        <Slider
          {...this.props}
          className={css}
          onChange={this.handleSliderValueChange}
          value={sliderInternalValue}
          labelRenderer={this.basicRenderer}
          min={min}
          max={max}
          disabled={readOnly || disabled}
        />
      </span>
    )
  }

  private handleDragStart = (event: SyntheticEvent<HTMLSpanElement>) => {
    event.stopPropagation()
  }

  private handleNumericInputChange = (event: SyntheticEvent<HTMLInputElement>) => {
    const newValue = Math.floor(Number(event.currentTarget.value))
    const { advancedOffset = 0 } = this.props

    const sliderInternalValue = this.calculateValue(newValue, this.state.min, this.state.max, advancedOffset)
    const calculatedOutputValue = this.calculateValue(newValue, 0, SLIDER_DEFAULT_MAX_VALUE)

    this.setState({
      ...this.state,
      calculatedOutputValue,
      sliderInternalValue,
    })

    if (isPresent(this.props.onChange)) {
      this.props.onChange(calculatedOutputValue)
    }
  }

  private handleToggleInputMethodClick = () => {
    this.setState({
      ...this.state,
      toggleInputMethod: !this.state.toggleInputMethod,
    })
  }

  private handleSliderValueChange = (value: number) => {
    this.setState({
      ...this.state,
      sliderInternalValue: value,
      calculatedOutputValue: value,
    })

    if (isPresent(this.props.onChange)) {
      this.props.onChange(value)
    }
  }

  private handleAdvancedSliderValueChange = (value: number) => {
    const { advancedOffset = 0 } = this.props
    const calculatedValue = advancedOffset + value

    this.setState({
      ...this.state,
      sliderInternalValue: value,
      calculatedOutputValue: calculatedValue,
    })

    if (isPresent(this.props.onChange)) {
      this.props.onChange(advancedOffset + value) // value can be below new zero
    }
  }

  private basicRenderer = (value: number) => {
    return `${value}%`
  }

  private advancedRenderer = (value: number) => {
    const { advancedOffset = 0 } = this.props
    const calculatedValue = advancedOffset + value

    return `${calculatedValue}%`
  }

  private calculateValue = (newValue: number, min: number, max: number, advancedOffset = 0) => {
    const calculatedValue = newValue - advancedOffset

    if (calculatedValue > max) {
      return max
    }

    if (calculatedValue < min) {
      return min
    }

    return calculatedValue
  }

  private calculateNewState = (value: number, offset: number, max: number) => {
    const off = offset
    const val = value

    const newMin = -off
    const newMax = max - off

    return {
      ...this.state,
      sliderInternalValue: val - off,
      calculatedOutputValue: val,
      min: newMin,
      max: newMax,
    } as State
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/simple-slider/simple-slider.tsx